import axios from 'axios';
import environment from 'environment';

import { JsonResponse, ListResponse, Pagination } from 'core/models/response';
import { User } from '../models/user';
import { ShareTransaction, ShareTransactionsResponse } from '../models/share-transaction';
import { InvestmentsResponse } from '../models/investment';
import { CompaniesResponse, CompanyDocumentsResponse, CompanyOverview } from '../models/companies';
import { WalletTransactionsResponse } from '../models/wallet-transactions';
import { BankAccountsResponse } from '../models/bank-account';
import { PaymentsResponse } from '../models/payment';
import { WithdrawRequest, WithdrawRequestsResponse } from '../models/withdraw-request';
import { WalletBalance } from '../models/wallet-balance';
import { PointWalletBalance, PointWalletTransaction, PointWalletTransactionsResponse } from '../models/point-wallet-transactions';

interface UsersResponse {
	users: User[];
	pagination: Pagination;
}

export interface CreateUserPayload extends Omit<
	User,
	| 'id'
	| 'is_phone_verified'
	| 'phone_verified_at'
	| 'country'
	| 'created_at'
	| 'updated_at'
	| 'profile_status_reason'
	| 'read_latest_agreement_terms'
	| 'is_active'
	| 'avatar_preview'
	| 'avatar_url'
	| 'default_user_signature_preview'
	| 'default_user_signature_url'
> {
	password: string;
	password_confirmation: string;
	country_id: number;
}

export interface UpdateUserPayload extends CreateUserPayload {
	profile_status_reason?: string;
}

export const onGetUsers = (page: number = 1, search?: string) => {
	return axios.get<JsonResponse<UsersResponse>>(`${environment.BASE_API_ENDPOINT}/admin/users`, {
		params: {
			page,
			search,
		},
	});
};

export const onGetUser = (userId: string) => {
	return axios.get<JsonResponse<User>>(`${environment.BASE_API_ENDPOINT}/admin/users/${userId}`);
};

export const onCreateUser = (userData: CreateUserPayload) => {
	return axios.post<JsonResponse<User>>(`${environment.BASE_API_ENDPOINT}/admin/users`, userData);
};

export const onUpdateUser = (userId: string, userData: Partial<CreateUserPayload>) => {
	return axios.put<JsonResponse<User>>(`${environment.BASE_API_ENDPOINT}/admin/users/${userId}`, userData);
};

export const onDeleteUser = (userId: number) => {
	return axios.delete(`${environment.BASE_API_ENDPOINT}/admin/users/${userId}`);
};

export const onGetShareTransactions = (userId: number, page = 1, status?: string | null) => {
	return axios.get<ShareTransactionsResponse>(`${environment.BASE_API_ENDPOINT}/admin/users/${userId}/share-transactions`, {
		params: { page, status },
	});
};

export const onGetShareTransactionDetails = (transactionId: number, userId: number) => {
	return axios.get<JsonResponse<ShareTransaction>>(
		`${environment.BASE_API_ENDPOINT}/admin/users/${userId}/share-transactions/${transactionId}`,
	);
};

export const onGetInvestments = (userId: number, page = 1) => {
	return axios.get<InvestmentsResponse>(`${environment.BASE_API_ENDPOINT}/admin/users/${userId}/investments`, { params: { page } });
};

export const onGetCompanies = (userId: number, page = 1) => {
	return axios.get<CompaniesResponse>(`${environment.BASE_API_ENDPOINT}/admin/users/${userId}/companies`, { params: { page } });
};

export const onGetCompanyOverview = (userId: number, companyId: number) => {
	return axios.get<JsonResponse<CompanyOverview>>(`${environment.BASE_API_ENDPOINT}/admin/users/${userId}/companies/overview/${companyId}`);
};

export const onGetCompanyDocuments = (userId: number, companyId: number) => {
	return axios.get<JsonResponse<CompanyDocumentsResponse>>(
		`${environment.BASE_API_ENDPOINT}/admin/users/${userId}/companies/documents/${companyId}`,
	);
};

export const onGetWalletTransactions = (userId: number, page = 1) => {
	return axios.get<JsonResponse<WalletTransactionsResponse>>(`${environment.BASE_API_ENDPOINT}/admin/users/${userId}/wallet-transactions`, {
		params: { page },
	});
};

export const onGetBanks = (userId: number, page = 1) => {
	return axios.get<JsonResponse<BankAccountsResponse>>(`${environment.BASE_API_ENDPOINT}/admin/users/${userId}/banks`, {
		params: { page },
	});
};

export const onGetPayments = (userId: number, page = 1) => {
	return axios.get<JsonResponse<PaymentsResponse>>(`${environment.BASE_API_ENDPOINT}/admin/users/${userId}/payments`, { params: { page } });
};

export const onGetWithdrawRequests = (userId: number, page = 1) => {
	return axios.get<JsonResponse<WithdrawRequestsResponse>>(
		`${environment.BASE_API_ENDPOINT}/admin/users/${userId}/balance-withdraw-requests`,
		{ params: { page } },
	);
};

export const onGetUserBalance = (userId: string) => {
	return axios.get<JsonResponse<WalletBalance>>(`${environment.BASE_API_ENDPOINT}/admin/users/${userId}/wallet-balance`);
};

export const onRejectWithdrawRequests = (requestId: number, rejection_reason: string) => {
	return axios.put(`${environment.BASE_API_ENDPOINT}/admin/users/reject-balance-withdraw-request/${requestId}`, { rejection_reason });
};

export const onCompleteWithdrawRequests = (requestId: number) => {
	return axios.put(`${environment.BASE_API_ENDPOINT}/admin/users/complete-balance-withdraw-request/${requestId}`);
};

export const onDeleteWithdrawRequests = (requestId: number) => {
	return axios.delete(`${environment.BASE_API_ENDPOINT}/admin/users/delete-balance-withdraw-request/${requestId}`);
};

export const onGetPointWalletTransactions = (userId: number, page = 1) => {
	return axios.get<JsonResponse<PointWalletTransactionsResponse>>(
		`${environment.BASE_API_ENDPOINT}/admin/users/${userId}/point-wallet-transactions`,
		{ params: { page } },
	);
};

export const onGetPointWalletBalance = (userId: number) => {
	return axios.get<JsonResponse<PointWalletBalance>>(`${environment.BASE_API_ENDPOINT}/admin/users/${userId}/point-wallet-balance`);
};

export const onChargePointWallet = (userId: number, amount: number) => {
	return axios.post(`${environment.BASE_API_ENDPOINT}/admin/users/${userId}/point-wallet-balance-charge`, { amount });
};
